import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { IMAGES } from "../data/vehicles";
import {
  fadeInUp,
  stagger,
  HERO_EYEBROW,
  HERO_TITLE,
  HERO_SUB_LATE,
  REVEAL_FROM_LEFT,
  REVEAL_UP,
} from "../lib/animations";

const PRINCIPLES = [
  {
    title: "The Analog Revolution",
    description: "Direct, mechanical connection is superior to digital isolation, nanny-systems, screens, and immature high-tech toys. We build cars that talk to you through the steering wheel, the pedals, and the shifter — not through a touchscreen.",
  },
  {
    title: "Engineering Honesty",
    description: "We use proven, over-engineered components: racing transmissions, dry sumps, forged internals. No fragile proprietary tech that requires dealer visits. Everything can be serviced with basic tools and honest work.",
  },
  {
    title: "Resourcefulness Over Bloat",
    description: "We partner with specialists like Jim Wolf Technology to deliver 800 horsepower without bloated R&D budgets. The result: world-class performance at prices that embarrass the establishment.",
  },
  {
    title: "True Safety Philosophy",
    description: "Fixed racing seats and 4-point harnesses represent the next evolution in occupant protection. We're moving beyond the outdated paradigm of relying on airbags and electronic nannies.",
  },
  {
    title: "Built to Last 100 Years",
    description: "Every Keller Motors vehicle is designed for indefinite production. Nothing will ever fundamentally change — just refined designs and the occasional revision if problems arise. Your grandchildren should be able to buy the same car.",
  },
  {
    title: "No Profit From Inconvenience",
    description: "We pledge to never profit from parts or service. When our customers are inconvenienced, we should not benefit. Period.",
  }
];

const HeroSection = () => (
  <section className="relative h-[70vh] min-h-[500px] overflow-hidden" data-testid="philosophy-hero">
    <div className="absolute inset-0">
      <img 
        src={IMAGES.engine.block}
        alt="Engine"
        className="w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/50" />
    </div>

    <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-8 pt-24">
      <div className="max-w-[1400px] mx-auto w-full">
        <motion.p 
          className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-[#FF2A00] mb-4"
          {...HERO_EYEBROW}
        >
          The Manifesto
        </motion.p>
        <motion.h1 
          className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-6"
          {...HERO_TITLE}
        >
          WHY WE BUILD
        </motion.h1>
        <motion.p 
          className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl"
          {...HERO_SUB_LATE}
        >
          The automotive industry has lost its way. We're here to bring it back.
        </motion.p>
      </div>
    </div>
  </section>
);

const MarqueeSection = () => (
  <div className="py-4 bg-[#0F0F0F] border-y border-[#262626] overflow-hidden">
    <Marquee speed={40} gradient={false}>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mx-8">CABLE-OPERATED THROTTLE</span>
      <span className="text-[#262626] mx-4">•</span>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mx-8">GATED SHIFTER</span>
      <span className="text-[#262626] mx-4">•</span>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mx-8">ANALOG GAUGES</span>
      <span className="text-[#262626] mx-4">•</span>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mx-8">MECHANICAL SUSPENSION</span>
      <span className="text-[#262626] mx-4">•</span>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mx-8">NO SCREENS</span>
      <span className="text-[#262626] mx-4">•</span>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mx-8">MANUAL TRANSMISSION</span>
      <span className="text-[#262626] mx-4">•</span>
    </Marquee>
  </div>
);

const PrinciplesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8" data-testid="principles-section">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="mb-16"
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-4">
            Core Principles
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-6xl tracking-tight">
            WHAT WE BELIEVE
          </motion.h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {PRINCIPLES.map((principle) => (
            <motion.div
              key={principle.title}
              initial={REVEAL_UP.initial}
              animate={isInView ? REVEAL_UP.target : {}}
              transition={REVEAL_UP.transition}
              className="border border-[#262626] p-8 hover:border-[#FF2A00] transition-colors duration-300"
            >
              <h3 className="font-display text-2xl mb-4">{principle.title}</h3>
              <p className="text-[#A3A3A3] leading-relaxed">{principle.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const EngineInstallSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8 bg-[#0F0F0F]" data-testid="engine-install-section">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-12 md:gap-24 items-center">
        <motion.div
          initial={REVEAL_FROM_LEFT.initial}
          animate={isInView ? REVEAL_FROM_LEFT.target : {}}
          transition={REVEAL_FROM_LEFT.transition}
        >
          <img 
            src={IMAGES.engine.dressed}
            alt="Engine Installation"
            className="w-full h-auto border border-[#262626]"
          />
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            The Experience
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-5xl tracking-tight mb-8">
            A WHOLE NEW ERA OF CAR OWNERSHIP
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-[#A3A3A3] leading-relaxed mb-6">
            When you buy a Keller, your ownership experience starts before you even leave the dealership. 
            Every Keller Motors customer participates in the installation of their engine, working alongside 
            our certified technicians. While our competitors lock you out with proprietary software and 
            patented bolts, we equip you with the knowledge and resources to service your car however 
            YOU decide is best.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-white leading-relaxed mb-8">
            This isn't just a clever delivery experience — it's a rite of passage.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/vehicles" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-[#FF2A00] hover:text-white transition-colors">
              See the Vehicles <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const AntiEVSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-40 px-6 md:px-8" data-testid="anti-ev-section">
      <div className="max-w-[1400px] mx-auto text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            Our Position
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-8">
            PRO-OIL.<br />ANTI-LITHIUM.
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#A3A3A3] max-w-3xl mx-auto mb-8">
            We are unapologetically pro-internal-combustion-engine. We believe the automobile reached 
            a point of perfection in mechanical simplicity — and then the industry abandoned it in 
            pursuit of profit and planned obsolescence. We are bringing that simplicity back, and 
            putting our customers first.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white max-w-3xl mx-auto">
            Our objective: to produce radically exciting and affordable sports cars while sparking 
            a movement that causes every driver to rethink what they actually need from a car — 
            transportation and excitement.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default function PhilosophyPage() {
  return (
    <main className="min-h-screen bg-[#050505]">
      <Header />
      <HeroSection />
      <MarqueeSection />
      <PrinciplesSection />
      <div className="section-divider max-w-[1400px] mx-auto" />
      <EngineInstallSection />
      <AntiEVSection />
      <Footer />
    </main>
  );
}
